import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { isPortalTask } from "components/tasks/taskMeta";
import type { AppTenantFields } from "modules/apps/tenants";
import { useMemberGetQuery, useMemberMutate } from "modules/members/api";
import type { MFApprovalRequestLine } from "state/members/access_v2/catalogs";
import type { MFIdentity } from "state/members/member";
import { wroteAccounts } from "state/members/offboardingWrites";
import { isPasskeyApprovalRefusal } from "state/members/stepUpFailure";

// ---------------------------------------------------------------------------
// Flat task list — the aggregated "everything in one list" feed (access
// approvals, provisioning/deprovisioning, batches, entitlement adjustments,
// offboarding, access-review campaigns). Served by GET /members/tasks, which
// mirrors the extension's /extension/members/tasks. Shape ported from the
// extension's `Task` type.
// ---------------------------------------------------------------------------
export type MFTaskItemType =
  "assignment" | "offboarding" | "access_review_campaign";

export type MFTaskEntityType =
  | "access_approval"
  | "provisioning"
  | "deprovisioning"
  | "access_provisioning_batch"
  | "entitlement_adjustment"
  | "indirect_access_remediation"
  | "entitlement_provisioning"
  | "entitlement_deprovisioning"
  | "access_review_campaign"
  | "offboarding"
  | (string & {});

export type MFTaskItem = {
  taskType: MFTaskItemType;
  assigneeId?: string;
  instanceId?: string;
  entityId?: string;
  entityType: MFTaskEntityType;
  title: string;
  body?: string | null;
  status: string;
  createdAt?: string;
  resourceImageUrl?: string | null;
  memberImageUrl?: string | null;
  memberName?: string | null;
  memberEmail?: string | null;
  memberJobTitle?: string | null;
  offboardingId?: string;
  accountCount?: number;
  /** Offboarding only — completed accounts (for the progress ring). */
  accountsDone?: number;
  dueDate?: string;
  campaignCycleId?: string;
  /** Completed tasks only — the resolution + when it closed. */
  result?: string | null;
  completedAt?: string;
  /**
   * An access approval whose stage needs the approver's passkey: Approve opens
   * the signing page, since the server refuses an inline answer. Deny never
   * needs one. Absent from servers that predate the flag.
   */
  passkeyRequired?: boolean;
};

// The flat feed lives under its own key family ["mf","tasksFeed", …] (active,
// completed, detail, offboarding all share this prefix so one prefix op covers
// them). Kept distinct from the older ["mf","tasks"] task-list namespace.
const useListTasks = (): { data: MFTaskItem[]; isLoading: boolean } => {
  const query = useMemberGetQuery<MFTaskItem[]>(
    ["mf", "tasksFeed"],
    "/members/tasks",
    // Non-suspense + no error boundary: this is hoisted into the Home dashboard
    // alongside other cards, so a /members/tasks hiccup must degrade to an empty
    // Tasks card — NOT throw to the route error boundary and blank all of Home
    // (the per-card <ErrorWrapper> can't catch a hoisted fetch).
    { suspense: false, useErrorBoundary: false },
  );
  // Hide batch/campaign rows from the personal feed (see isPortalTask). Memoised
  // so consumers' downstream memos (counts/filtering) keep a stable reference.
  const data = useMemo(
    () => (query.data || []).filter((t) => isPortalTask(t.entityType)),
    [query.data],
  );
  return { data, isLoading: query.isLoading };
};

// Completed history — the member's own resolved assignment tasks (bounded to
// the 100 most recent server-side). Keyed under ["mf","tasksFeed", …] so the
// respond/save cache ops (which target the tasksFeed prefix) refresh it too.
const useListCompletedTasks = (): {
  data: MFTaskItem[];
  isLoading: boolean;
} => {
  const query = useMemberGetQuery<MFTaskItem[]>(
    ["mf", "tasksFeed", "completed"],
    "/members/tasks/completed",
    // Non-suspense + no error boundary: the secondary tab powers the tab count;
    // it should load in the background and degrade to an empty list on error
    // rather than block/blank the page.
    { suspense: false, useErrorBoundary: false },
  );
  // Mirror the active feed — batch/campaign history is hidden here too.
  const data = useMemo(
    () => (query.data || []).filter((t) => isPortalTask(t.entityType)),
    [query.data],
  );
  return { data, isLoading: query.isLoading };
};

// ---------------------------------------------------------------------------
// Task detail (single task) — served by GET /members/tasks/:assigneeId (which
// delegates to the extension's `show`) and GET /members/tasks/offboarding/:id.
// Shapes ported from the extension's TaskDetail/OffboardingDetail. The requester
// carries a few extra HR fields (managerName / status / employmentStartDate)
// that the server exposes ONLY on the access-approval requester — see
// TaskView.render_requester. Other member renders stay on the minimal allowlist.
// ---------------------------------------------------------------------------
export type MFTaskMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  imageUrl?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  locationCity?: string | null;
  locationCountry?: string | null;
  // Requester-only extras (access_approval).
  managerName?: string | null;
  status?: string | null;
  employmentStartDate?: string | null;
};

// NHI requester descriptor — present on an access approval whose requester is a
// non-human identity (service account / agent) rather than a member. Mirrors the
// server's render_requested_by_identity ({id, name, type}).
export type MFTaskIdentity = {
  id?: string;
  name: string;
  type?: string | null;
};

export type MFSelectableResource = AppTenantFields & {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  resource?:
    | (AppTenantFields & {
        id: string;
        name: string;
        type: string;
        imageUrl?: string | null;
      })
    | null;
};

// Aggregate decision context for an access approval (counts only — no
// requester/reviewer/member identities). `recent` is newest-first, capped at 6.
export type MFPriorRequests = {
  approved: number;
  denied: number;
  recent: ("approved" | "denied")[];
};

export type MFTeamAdoption = {
  group: string;
  members: number;
  withAccess: number;
  pct: number;
};

export type MFAccessApprovalEntity = {
  id: string;
  status: string;
  // Null when the requester is a non-human identity (NHI access request) — use
  // requestedByIdentity to render the requester in that case.
  requester: MFTaskMember | null;
  requestedBy?: MFTaskMember | null;
  requestedByIdentity?: MFTaskIdentity | null;
  // The account/NHI the grant targets (PLO-4704) — null for a default-account
  // request. Distinct from requestedByIdentity (who raised it).
  requestedIdentity?: MFIdentity | null;
  selectableResource?: MFSelectableResource | null;
  catalogName?: string | null;
  catalogItemName?: string | null;
  requestedForSeconds?: number | null;
  requestedIndefinite?: boolean | null;
  requestComments?: string | null;
  requestLocation?: string | null;
  requestedAt?: string | null;
  aiInsight?: string | null;
  priorRequests?: MFPriorRequests | null;
  teamAdoption?: MFTeamAdoption | null;
  lines?: MFApprovalRequestLine[] | null;
};

export type MFTaskEntitlement = {
  id: string;
  name: string;
  type?: string | null;
  description?: string | null;
};

export type MFResourceAccessEntity = {
  id?: string;
  resourceName?: string | null;
  resourceType?: string | null;
  resourceDomain?: string | null;
  resourceDeeplink?: string | null;
  resourceImageUrl?: string | null;
  selectableResourceName?: string | null;
  catalogItemName?: string | null;
  catalogName?: string | null;
  member?: MFTaskMember | null;
  grantedAt?: string | null;
  expiresAt?: string | null;
  requestComments?: string | null;
  requestedForSeconds?: number | null;
  requestedIndefinite?: boolean | null;
  entitlement?: MFTaskEntitlement | null;
  integrationName?: string | null;
  connectionName?: string | null;
  accountIdentifier?: string | null;
};

export type MFTaskEntity =
  MFAccessApprovalEntity | MFResourceAccessEntity | Record<string, unknown>;

export type MFTaskDetail = MFTaskItem & {
  entity?: MFTaskEntity | null;
  metadata?: Record<string, unknown> | null;
};

export type MFApp = AppTenantFields & {
  id: string;
  name: string;
  imageUrl?: string | null;
};

export type MFOffboardingChecklist = {
  loginBlockedAt?: string | null;
  oauthTokensRevokedAt?: string | null;
  adminRightsConfirmedAt?: string | null;
  accountRemovedAt?: string | null;
  adminTransferNotRequired?: boolean | null;
};

export type MFOffboardingAccount = {
  offboardingAccountId: string;
  status: "incomplete" | "complete" | "declined" | "cancelled";
  app: MFApp;
  checklist: MFOffboardingChecklist;
};

export type MFOffboardingDetail = {
  taskType: "offboarding";
  offboardingId: string;
  status?: string | null;
  dueDate?: string | null;
  leaveDate?: string | null;
  accountsTotal?: number | null;
  accountsRemaining?: number | null;
  title: string;
  offboardingMember: MFTaskMember;
  accounts: MFOffboardingAccount[];
};

// Detail is fetched lazily — only when a drawer is open (enabled on a real id).
// `suspense: false` is critical: the app's QueryClient defaults to suspense
// mode, so without this the fetch on drawer-open would suspend the page-level
// <Suspense> (blanking the whole page) instead of showing the drawer's own
// inline spinner. `useErrorBoundary: false` is equally critical: the client
// also defaults useErrorBoundary:true, which would throw a failed detail fetch
// to the route boundary (blanking the page) instead of the drawer's inline
// "Couldn't load" / isError fallback.
const useGetTaskDetail = (assigneeId?: string) => {
  const query = useMemberGetQuery<MFTaskDetail>(
    ["mf", "tasksFeed", "detail", assigneeId ?? ""],
    `/members/tasks/${assigneeId ?? ""}`,
    { enabled: Boolean(assigneeId), suspense: false, useErrorBoundary: false },
  );
  return {
    data: query.data,
    // isInitialLoading (not isLoading): a *disabled* query reports isLoading
    // true in v4, which would show a perpetual spinner for tasks with no
    // assigneeId (e.g. campaigns). This is true only while actively fetching.
    isLoading: query.isInitialLoading,
    isError: query.isError,
  };
};

const useGetOffboardingDetail = (offboardingId?: string) => {
  const query = useMemberGetQuery<MFOffboardingDetail>(
    ["mf", "tasksFeed", "offboarding", offboardingId ?? ""],
    `/members/tasks/offboarding/${offboardingId ?? ""}`,
    // suspense:false + useErrorBoundary:false — see useGetTaskDetail: keep the
    // drawer's inline spinner / "Couldn't load" fallback instead of blanking.
    {
      enabled: Boolean(offboardingId),
      suspense: false,
      useErrorBoundary: false,
    },
  );
  return {
    data: query.data,
    // isInitialLoading: only true while actively fetching (disabled queries
    // report isLoading true in v4 — see useGetTaskDetail).
    isLoading: query.isInitialLoading,
    isError: query.isError,
  };
};

export type TaskDecision = "approve" | "deny";

// Respond to a single actionable task (approve/deny). assigneeId comes from the
// task row; the hook is created per row, mirroring useDecideExtension/useCancelRequest.
const useRespondToTask = (assigneeId?: string) => {
  const queryClient = useQueryClient();
  // NOTE: we intentionally do NOT invalidate the feed here. The assignment is
  // completed asynchronously server-side (event-driven), so an immediate refetch
  // still returns the task as pending and re-adds it to the list. Instead we
  // optimistically drop it from the cached lists on success; the next natural
  // refetch (navigation / focus) reconciles once the server has caught up.
  // A server that predates `passkeyRequired` refuses an unsigned approval on a
  // passkey stage; the caller opens the signing page for it (approveTask), so
  // that refusal is not also shown as an error.
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/tasks/${assigneeId ?? ""}/respond`,
    [["mf", "me", "stats"]],
    { quietWhen: isPasskeyApprovalRefusal },
  );
  return {
    isLoading,
    respond: (decision: TaskDecision, comment?: string) =>
      mutate({ decision, comment }).then((res) => {
        if (assigneeId) {
          // Scope to the feed family only (active + completed). The prefix can't
          // reach the legacy task-lists cache anymore; detail/offboarding caches
          // hold an object so the Array guard leaves them untouched.
          queryClient.setQueriesData<
            { success?: boolean; data?: MFTaskItem[] } | undefined
          >({ queryKey: ["mf", "tasksFeed"] }, (old) => {
            if (!old || !Array.isArray(old.data)) return old;
            return {
              ...old,
              data: old.data.filter((t) => t.assigneeId !== assigneeId),
            };
          });
        }
        return res;
      }),
  };
};

// Offboarding account update — the single endpoint behind the drawer's
// checklist toggles (Admin transfer / Account removal groups), Save progress,
// and Refuse (`markDeclined` + reason). Mirrors MembersUpdateOffboardingAccountsInput.
export type MFOffboardingUpdate = {
  accountIds: string[];
  markLoginBlocked?: boolean;
  markAdminRightsTransferred?: boolean;
  markRemoved?: boolean;
  adminTransferNotRequired?: boolean;
  markDeclined?: boolean;
  declinedReason?: string;
};

// Resolves to whether every account asked for was written.
const useUpdateOffboardingAccounts = (offboardingId?: string) => {
  const { mutate, isLoading } = useMemberMutate<{ id: string }[]>(
    "POST",
    `/members/offboarding/${offboardingId ?? ""}/accounts`,
    // The ["mf","tasksFeed"] prefix already covers the active feed, completed
    // list AND this offboarding's detail (["mf","tasksFeed","offboarding",id]),
    // so one prefix key invalidates them all — no redundant per-id entry.
    [
      ["mf", "tasksFeed"],
      ["mf", "me", "stats"],
    ],
  );
  return {
    isLoading,
    update: (body: MFOffboardingUpdate) =>
      mutate({
        account_ids: body.accountIds,
        mark_login_blocked: body.markLoginBlocked,
        mark_admin_rights_transferred: body.markAdminRightsTransferred,
        mark_removed: body.markRemoved,
        admin_transfer_not_required: body.adminTransferNotRequired,
        mark_declined: body.markDeclined,
        declined_reason: body.declinedReason,
      }).then((res) => res.success && wroteAccounts(res.data, body.accountIds)),
  };
};

export const MF_TASKS = {
  useListTasks,
  useListCompletedTasks,
  useRespondToTask,
  useGetTaskDetail,
  useGetOffboardingDetail,
  useUpdateOffboardingAccounts,
};
